/**
 * @file CompanySessionsFilters.tsx
 * @brief Filter bar for company-scoped sessions — flush GridCell layout.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — end-to-end GridCell filters
 * @updated 01-10-2026 — the date moved to the page header's calendar circle;
 *          no Clear button; status / kind / employee became header pills
 *          (`CompanySessionsHeaderFilters`), this file's bar is the search
 */

import { useEffect, useState } from "react";
import { HeaderFilterMenu } from "@/components/ui/HeaderFilterMenu";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import type { SearchableMenuItem } from "@/components/ui/SearchableMenuList";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import type { CompanySessionsListParams } from "../schemas";
import { EmployeePicker } from "./EmployeePicker";

const ALL = "all";

const STATUS_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any status" },
  { value: "completed", label: "Completed" },
  { value: "in_progress", label: "In progress" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const KIND_ITEMS: SearchableMenuItem[] = [
  { value: ALL, label: "Any kind" },
  { value: "standard", label: "Standard" },
  { value: "realtime", label: "Realtime" },
];

type StatusFilter = NonNullable<CompanySessionsListParams["status"]> | typeof ALL;
type KindFilter = NonNullable<CompanySessionsListParams["kind"]> | typeof ALL;

interface CompanySessionsFiltersProps {
  value: CompanySessionsListParams;
  onChange: (next: CompanySessionsListParams) => void;
  /** A user's own list: no employee filter (01-10-2026). */
  hideEmployee?: boolean;
}

/**
 * Status, kind and employee as the page header's text pills, beside the date
 * circle (DES 01-10-2026: "inko bhi yehi karo, bus text saath show karna").
 */
export function CompanySessionsHeaderFilters({
  value,
  onChange,
  hideEmployee = false,
}: CompanySessionsFiltersProps) {
  const status: StatusFilter = (value.status ?? ALL) as StatusFilter;
  const kind: KindFilter = (value.kind ?? ALL) as KindFilter;
  const labelOf = (items: SearchableMenuItem[], v: string) =>
    items.find((i) => i.value === v)?.label ?? items[0].label;

  return (
    <>
      <HeaderFilterMenu
        title="Filter by status"
        searchPlaceholder="Search statuses"
        items={STATUS_ITEMS}
        selected={status}
        label={labelOf(STATUS_ITEMS, status)}
        active={status !== ALL}
        onClear={() => onChange({ ...value, status: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            status: v === ALL ? undefined : (v as CompanySessionsListParams["status"]),
            page: 1,
          })
        }
      />
      <HeaderFilterMenu
        title="Filter by kind"
        searchPlaceholder="Search kinds"
        items={KIND_ITEMS}
        selected={kind}
        label={labelOf(KIND_ITEMS, kind)}
        active={kind !== ALL}
        onClear={() => onChange({ ...value, kind: undefined, page: 1 })}
        onSelect={(v) =>
          onChange({
            ...value,
            kind: v === ALL ? undefined : (v as CompanySessionsListParams["kind"]),
            page: 1,
          })
        }
      />
      {hideEmployee ? null : (
        <EmployeePicker
          variant="pill"
          value={value.employee_id ?? null}
          onChange={(id) => onChange({ ...value, employee_id: id ?? undefined, page: 1 })}
        />
      )}
    </>
  );
}

/** The search, rendered inside the page's bottom `PageDock`. */
export function CompanySessionsFilters({
  value,
  onChange,
}: CompanySessionsFiltersProps) {
  const [searchInput, setSearchInput] = useState(value.q ?? "");
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  useEffect(() => {
    if ((value.q ?? "") === debouncedSearch) return;
    onChange({ ...value, q: debouncedSearch || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  // The Notes screen's search (DES 01-10-2026), in the page's bottom dock.
  return (
    <ExpandingSearch
      value={searchInput}
      onChange={setSearchInput}
      placeholder="Search by session ID, name, email, reference"
      label="Search sessions"
    />
  );
}
